import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, API, fmtMoney, fmtDate, fmtDateTime, LEGAL_FOOTER } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import {
  ArrowLeft, Pencil, Trash2, Download, Link as LinkIcon, ExternalLink, ShieldCheck, FileText, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [qr, s] = await Promise.all([api.get(`/quotes/${id}`), api.get('/settings')]);
      setQ(qr.data);
      setSettings(s.data);
    } catch {
      toast.error('No se pudo cargar el presupuesto');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isExpired = q ? new Date(q.expiry_date) < new Date() : false;

  const updateStatus = async (status) => {
    try {
      await api.put(`/quotes/${id}`, { status });
      toast.success('Estado actualizado');
      load();
    } catch { toast.error('No se pudo actualizar'); }
  };

  const refreshClientData = async () => {
    try {
      // Triggering an update without changing fields will re-snapshot the client
      // (backend always refreshes client_snapshot on PUT).
      await api.put(`/quotes/${id}`, { status: q.status });
      toast.success('Datos del cliente actualizados');
      load();
    } catch { toast.error('No se pudo actualizar'); }
  };

  const remove = async () => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    try { await api.delete(`/quotes/${id}`); toast.success('Eliminado'); navigate('/quotes'); }
    catch { toast.error('No se pudo eliminar'); }
  };

  const downloadPDF = async () => {
    try {
      const r = await api.get(`/quotes/${id}/pdf`, {
        responseType: 'blob',
        params: { v: q.updated_at || q.id },
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${q.quote_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo generar el PDF'); }
  };

  const publicUrl = q ? `https://cotizar.caura.net/verify/${q.public_id}` : '';
  const cacheBust = q ? `?v=${encodeURIComponent(q.updated_at || q.id)}` : '';
  const qrUrl = q ? `${API}/quotes/${q.id}/qr${cacheBust}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link público copiado');
    } catch { toast.error('No se pudo copiar'); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;
  if (!q) return null;

  return (
    <div>
      <PageHeader
        title={<span className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-[hsl(var(--primary))]" />
          <span className="font-mono">{q.quote_number}</span>
          <QuoteStatusBadge status={q.status} expired={isExpired} />
        </span>}
        subtitle={q.title || `Emitido para ${q.client_snapshot?.name}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/quotes"><Button variant="ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Volver</Button></Link>
            <Button variant="outline" onClick={() => navigate(`/quotes/${id}/edit`)} data-testid="quote-detail-edit-button"><Pencil className="mr-1.5 h-4 w-4" /> Editar</Button>
            <Button variant="outline" onClick={copyLink} data-testid="quote-detail-copy-link-button"><LinkIcon className="mr-1.5 h-4 w-4" /> Copiar link</Button>
            <Button className="btn-primary" onClick={downloadPDF} data-testid="quote-detail-pdf-button"><Download className="mr-1.5 h-4 w-4" /> Descargar PDF</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          {/* Emisor + Cliente */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Emisor</div>
                <div className="mt-2 font-display text-base font-semibold">{settings?.name}</div>
                <div className="font-mono text-xs text-muted-foreground">RIF: {settings?.rif}</div>
                <div className="mt-2 text-sm text-muted-foreground">{settings?.address}</div>
              </CardContent>
            </Card>
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Cliente</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={refreshClientData}
                    title="Refrescar datos del cliente desde su ficha actual"
                    data-testid="quote-detail-refresh-client"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Refrescar
                  </Button>
                </div>
                <div className="mt-2 font-display text-base font-semibold">{q.client_snapshot?.name}</div>
                {q.client_snapshot?.rif && <div className="font-mono text-xs text-muted-foreground">RIF/ID: {q.client_snapshot.rif}</div>}
                {q.client_snapshot?.contact_person && <div className="mt-1 text-sm">Atn: <span className="text-foreground">{q.client_snapshot.contact_person}</span></div>}
                {q.client_snapshot?.address && <div className="mt-2 text-sm text-muted-foreground">{q.client_snapshot.address}</div>}
                {q.client_snapshot?.email && <div className="text-sm text-muted-foreground">{q.client_snapshot.email}</div>}
                {q.client_snapshot?.phone && <div className="text-sm text-muted-foreground">{q.client_snapshot.phone}</div>}
              </CardContent>
            </Card>
          </div>

          {/* Items */}
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-5">
              <h3 className="font-display text-base font-semibold">Items</h3>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Descripción</th>
                      <th className="px-2 py-2 text-right">Cant.</th>
                      <th className="px-2 py-2 text-left">Unidad</th>
                      <th className="px-2 py-2 text-right">P. Unit</th>
                      <th className="px-2 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-[var(--glass-border)]">
                        <td className="px-2 py-2 font-mono">{idx + 1}</td>
                        <td className="px-2 py-2">{it.description}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{it.quantity}</td>
                        <td className="px-2 py-2">{it.unit}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtMoney(it.unit_price)}</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtMoney((it.quantity || 0) * (it.unit_price || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end">
                <div className="w-full max-w-xs space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono tabular-nums">{fmtMoney(q.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">IVA ({(q.tax_rate * 100).toFixed(0)}%)</span>
                    <span className="font-mono tabular-nums">{fmtMoney(q.tax_amount)}</span>
                  </div>
                  <div className="divider-accent my-1" />
                  <div className="flex items-center justify-between">
                    <span className="font-display text-base font-semibold">TOTAL</span>
                    <span data-testid="quote-total-amount" className="font-mono text-xl font-semibold tabular-nums text-[hsl(var(--primary))]">{fmtMoney(q.total, q.currency)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {q.notes && (
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notas</div>
                <p className="mt-2 whitespace-pre-line text-sm text-foreground">{q.notes}</p>
              </CardContent>
            </Card>
          )}

          <Card className="glass border-[rgba(85,117,245,0.30)] bg-[rgba(85,117,245,0.06)]">
            <CardContent className="p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">Condiciones de pago</div>
              <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-foreground" data-testid="quote-detail-legal-footer">{q.payment_terms || LEGAL_FOOTER}</p>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4 lg:col-span-4">
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="space-y-3 p-5">
              <h3 className="font-display text-base font-semibold">Estado</h3>
              <Select value={q.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="quote-detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-[var(--glass-border)] bg-[#0F1626]">
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="accepted">Aceptado</SelectItem>
                  <SelectItem value="rejected">Rechazado</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-muted-foreground">Emisión</div>
                  <div className="font-mono">{fmtDate(q.issue_date)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Vence</div>
                  <div className="font-mono">{fmtDate(q.expiry_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Vigencia</div>
                  <div className="font-mono">{q.validity_days} días</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="space-y-2.5 p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                <h3 className="font-display text-sm font-semibold">QR de verificación</h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex shrink-0 items-center justify-center rounded-md bg-white p-1.5">
                  <img src={qrUrl} alt="QR" className="h-24 w-24" data-testid="quote-detail-qr-image" />
                </div>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="break-all rounded bg-[var(--surface-1)] p-1.5 font-mono text-[9px] leading-tight text-muted-foreground">
                    {publicUrl}
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 flex-1 px-2 text-xs" onClick={copyLink} data-testid="quote-detail-copy-link-small">
                      <LinkIcon className="mr-1 h-3 w-3" /> Copiar
                    </Button>
                    <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" size="sm" className="h-7 w-full px-2 text-xs" data-testid="quote-detail-open-public-button">
                        <ExternalLink className="mr-1 h-3 w-3" /> Abrir
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
              <div className="rounded bg-[var(--surface-1)] p-1.5 text-[10px] leading-tight text-muted-foreground">
                <div>Folio: <span className="font-mono text-foreground">{q.public_id}</span></div>
                <div className="truncate">Hash: <span className="font-mono">{q.verification_hash?.slice(0, 20)}…</span></div>
              </div>
            </CardContent>
          </Card>

          <Button variant="ghost" className="w-full text-[#FF8FA3] hover:text-[#FF5C7A]" onClick={remove} data-testid="quote-detail-delete-button">
            <Trash2 className="mr-1.5 h-4 w-4" /> Eliminar presupuesto
          </Button>
          <div className="text-center text-[10px] text-muted-foreground">
            Creado: {fmtDateTime(q.created_at)} · Actualizado: {fmtDateTime(q.updated_at)}
          </div>
        </div>
      </div>
    </div>
  );
}
